<template>
  <div
    ref="containerRef"
    class="virtual-list"
    :class="{
      'virtual-list--vertical': isVertical,
      'virtual-list--horizontal': isHorizontal,
    }"
    :style="containerProps.style"
    v-bind="$attrs"
    @scroll="containerProps.onScroll"
  >
    <component
      :is="draggable ? 'draggable' : 'div'"
      v-if="hasItem"
      :disabled="!draggable"
      :list="draggable ? virtualList : []"
      class="virtual-list__items"
      v-bind="{ ...wrapperProps }"
      draggable-selector=".draggable-item"
      @sort="$emit('drag-event', $event)"
    >
      <SizeWrapper
        v-for="{ data, index: virtualScrollIndex } in virtualList"
        :key="data.id"
        class="virtual-list__items__item"
        :class="disableDragFor && disableDragFor({ id: data.id }) ? '' : 'draggable-item'"
        @height="setItemSize($event, virtualScrollIndex, 'height')"
        @width="setItemSize($event, virtualScrollIndex, 'width')"
      >
        <slot
          name="item"
          :item="data"
          :index="virtualScrollIndex"
        />
      </SizeWrapper>
    </component>
    <MissingSlot
      v-else
      slot-name="item"
      slot-parent="VirtalList"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, useSlots, watch } from 'vue'
import type { PropType } from 'vue'

import { useVirtualList } from '@vueuse/core'
import Draggable from '@/uiKit/Draggable/Draggable.vue'
import MissingSlot from '@/uiKit/MissingSlot/MissingSlot.vue'
import SizeWrapper from '@/uiKit/SizeWrapper/SizeWrapper.vue'
import { DEFAULT_OPTION_HEIGHT, DEFAULT_OPTION_WIDTH } from '@/uiKit/constants'

import type { VirtualListItemType } from './types'

type ItemSizeRecord = Partial<Record<string, { height?: number; width?: number }>>

export default defineComponent({
  name: 'VirtualList',
  components: {
    Draggable,
    MissingSlot,
    SizeWrapper,
  },
  props: {
    items: { required: true, type: Array as PropType<VirtualListItemType[]> },
    buffer: { default: 5, type: Number },
    draggable: { default: false, type: Boolean },
    disableDragFor: {
      default: null,
      required: false,
      type: Function as PropType<(item: VirtualListItemType) => boolean>,
    },
    direction: { default: 'vertical', type: String as PropType<'horizontal' | 'vertical'> },
    size: {
      required: false,
      default: 'dynamic',
      type: [String, Number, Function] as PropType<
        'dynamic' | 'inherit' | number | ((idx: number) => number)
      >,
    },
  },
  setup(props) {
    const slots = useSlots()

    /** Keeps track of the size of each item in the virtual list by its id.
     * Using a Map/Set as a data-structure won't work with Vue reactivity until we upgrade to Vue 3
     * https://stackoverflow.com/a/64512468
     */
    const itemSizes = ref<ItemSizeRecord>({})

    // SizeWrapper will only trigger an event for an item that has been added/updated
    // and is currently rendered by the virtual list.
    // This is used to detect if an item has been added/removed
    // and adjust the item sizes.
    watch(
      () => props.items,
      (newItems, oldItems = []) => {
        // initialize the item sizes
        if (oldItems?.length === 0) {
          itemSizes.value = props.items.reduce((accumulator: ItemSizeRecord, currItem) => {
            accumulator[currItem.id] = {}
            return accumulator
          }, {})
          return
        }

        /** Used to track which item sizes need to be removed
         * by initially copying all the existing item ids
         * and then removing each one from the new items that exists already
         * until only the ones left are the ones are missing from the new items. */
        const removedItemIds = new Set(Object.keys(itemSizes.value))
        newItems.forEach((newItem) => {
          // item added
          if (itemSizes.value[newItem.id] === undefined) {
            itemSizes.value[newItem.id] = {}
          } else {
            removedItemIds.delete(newItem.id.toString())
          }
        })

        removedItemIds.forEach((id) => {
          itemSizes.value[id] = undefined
        })
      },
      { immediate: true },
    )

    // if a custom item slot has been used
    const hasItem = computed(() => !!slots['item'])

    const isVertical = computed(() => props.direction === 'vertical')

    const isHorizontal = computed(() => props.direction === 'horizontal')

    // passing directly the props will throw the error
    // 'invalid watch source' so we use a computed method for that
    const computedItems = computed(() => props.items)

    const setItemSize = (val: number, idx: number, attr: 'height' | 'width'): void => {
      if (!val) {
        return
      }
      const id = computedItems.value[idx]?.id
      itemSizes.value[id] = { ...itemSizes.value[id], [attr]: val }
    }

    const getSize = (idx: number): number => {
      if (typeof props.size === 'function') {
        return props.size(idx)
      }
      if (props.size === 'dynamic') {
        const id = computedItems.value[idx]?.id
        return isVertical.value
          ? itemSizes.value[id]?.height || DEFAULT_OPTION_HEIGHT
          : itemSizes.value[id]?.width || DEFAULT_OPTION_WIDTH
      }
      if (props.size === 'inherit') {
        // In some cases, the height can't be calculated correctly.
        // Returning -1 forces the virtual scroller to use the element height without overriding it,
        // avoiding an issue where the scroll item is vertically squashed.
        return -1
      }
      return props.size
    }

    const {
      list: virtualList,
      containerProps,
      wrapperProps,
      scrollTo,
    } = useVirtualList(
      computedItems,
      isVertical.value
        ? {
            overscan: props.buffer,
            itemHeight: (idx): number => getSize(idx),
          }
        : {
            overscan: props.buffer,
            itemWidth: (idx): number => getSize(idx),
          },
    )
    const containerRef = containerProps.ref

    return {
      hasItem,
      isVertical,
      isHorizontal,
      setItemSize,

      // virtual list
      virtualList,
      containerProps,
      containerRef,
      wrapperProps,
      scrollTo,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/shared/scrollbarV2';

.virtual-list {
  height: 100%;
  min-height: inherit;
  margin: 0;
  padding: 0;

  &--vertical {
    max-height: 100%;
    width: 100%;
    @include scrollbarV2('vertical');
  }

  &--horizontal {
    max-width: 100%;
    height: 100%;
    @include scrollbarV2('horizontal');
  }

  &__items {
    width: 100%;

    &__item {
      position: relative;
      max-width: 100%;
      max-height: 100%;
      cursor: pointer;

      // the virtual list default display is block
      // which breaks the getSize behavior
      &:deep(.size-wrapper__item > div) {
        display: flex;
      }
    }
  }
}
</style>
